import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DOCUMENT,
  EventEmitter,
  inject,
  Input,
  model,
  OnInit,
  Output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, QueryParamsHandling, RouterLink } from '@angular/router';
import { FilledContentRelationshipField, FilledLinkToWebField, LinkField, LinkType } from '@prismicio/client';
import { ENVIRONMENT_URLS_CONFIG_TOKEN } from 'app-initialization';
import { SvgComponent } from 'icon';
import { Size, Style, Type } from 'interfaces';
import { LanguageService } from 'language';
import { ModalsService } from 'modal';
import { checkForAnchorTagLink, isNewLandingPage } from 'prismic';
import { App } from 'utils';

@Component({
  selector: 'lib-link',
  templateUrl: './link.component.html',
  styleUrls: ['./link.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SvgComponent, NgTemplateOutlet, RouterLink],
})
export class LinkComponent implements OnInit {
  private document = inject<Document>(DOCUMENT);
  private config = inject(ENVIRONMENT_URLS_CONFIG_TOKEN);
  private modalsService = inject(ModalsService);
  private route = inject(ActivatedRoute);
  private languageService = inject(LanguageService);
  public readonly LinkType = LinkType;
  public isAnchorTagLink = signal(false);
  public link = model<LinkField | FilledContentRelationshipField>();
  @Input() public title = '';
  @Input() public ariaLabel = '';
  @Input() public queryParamsHandling: QueryParamsHandling | null;
  @Input() public queryParams: object;
  @Input() public icon: string;
  @Input() public iconPosition: 'prefix' | 'suffix' = 'prefix';
  @Input() public linkType: Type = 'default';
  @Input() public linkStyle: Style | Style[] = 'default';
  @Input() public linkSize: Size = 'default';

  @Input()
  public get cssClass() {
    return `link ${this._cssClass} ${this.typeClass} ${this.styleClass} ${this.sizeClass}`;
  }

  public set cssClass(value: string) {
    this._cssClass = value;
  }

  private _cssClass = '';
  @Input() public textCssClass = '';
  @Output() public onClick = new EventEmitter<never>();

  public get typeClass() {
    switch (this.linkType) {
      case 'primary':
        return 'text-primary';
      case 'featured':
        return 'text-featured';
      case 'secondary':
        return 'text-gray-2';
      default:
        return '';
    }
  }

  public get styleClass(): string {
    if (Array.isArray(this.linkStyle)) {
      return this.linkStyle
        .map(style => {
          return this._getStyleClass(style);
        })
        .join(' ');
    }

    return this._getStyleClass(this.linkStyle);
  }

  private _getStyleClass(style: Style) {
    switch (style) {
      case 'bold':
        return 'font-bold';
      case 'underline':
        return 'underline';
      default:
        return '';
    }
  }

  public get sizeClass() {
    switch (this.linkSize) {
      case 'small':
        return 'text-sm';
      case 'large':
        return 'text-lg';
      case 'extra large':
        return 'text-xl';
      default:
        return '';
    }
  }

  public get target() {
    switch (this.link().link_type) {
      case LinkType.Web:
        return (this.link() as FilledLinkToWebField).target ?? '_self';
      case LinkType.Media:
        return '_blank';
      default:
        return null;
    }
  }

  public get documentType() {
    return (this.link() as FilledContentRelationshipField).type;
  }

  public get isValidLink() {
    return this.link() && this.link().link_type !== LinkType.Any;
  }

  public get hasPrefixIcon() {
    return this.icon && this.iconPosition === 'prefix';
  }

  public get hasSuffixIcon() {
    return this.icon && this.iconPosition === 'suffix';
  }

  public url = computed(() => {
    switch (this.link().link_type) {
      case LinkType.Media:
      case LinkType.Web:
        return (this.link() as FilledLinkToWebField).url;
      case LinkType.Document:
        return [
          '/',
          this.languageService.currentLanguage() ?? this.languageService.current,
          ...(this.link() as FilledContentRelationshipField).slug.split('/'),
        ];
      default:
        return null;
    }
  });

  public ngOnInit() {
    if (checkForAnchorTagLink(this.link())) {
      this.isAnchorTagLink.set(true);
    }

    // If app is not website and the link is a new landing page, use web link instead of router
    this.route.data.subscribe(data => {
      if (isNewLandingPage(this.link()) && data.app && data.app !== App.website) {
        this.link.set({
          url: `${this.config.websiteUrl}/${this.languageService.currentLanguage() ?? this.languageService.current}/${
            (this.link() as FilledContentRelationshipField).slug
          }`,
          link_type: LinkType.Web,
        });
      }
    });
  }

  public scrollTo(event: { preventDefault: () => void }, id: string | string[]): void {
    event.preventDefault();
    if (typeof id === 'string') {
      let anchorLink = '';
      if (id.startsWith('#')) {
        anchorLink = id.replace('#', '');
      }
      if (id.startsWith('https://#')) {
        anchorLink = id.replace('https://#', '');
      }
      (this.document.getElementById(anchorLink) as HTMLElement).scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }
  }

  public onMediaOrWebLinkClick() {
    this.onClick.emit();
  }

  public onDocumentLinkClick() {
    if (this.documentType === 'modal_kitsune') {
      this.modalsService.openModal((this.link() as FilledContentRelationshipField).id);
    }
    this.onClick.emit();
  }
}
